import React, { useState, useEffect } from "react";
import {
  Button,
  Drawer,
  Form,
  Input,
  Select,
  Row,
  Col,
  Tag,
  message,
  Image,
  Upload,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import API from "../../api";
import Barcode from "react-barcode";
import { UploadOutlined } from "@ant-design/icons";

const { Option } = Select;

const ProductDrawer = ({ open, setOpen, isEdit, initialValue, onSubmit }) => {
  const [form] = Form.useForm();
  const [brand, setBrand] = useState([]);
  const [stock, setStock] = useState([]);
  const [Offer, setOffer] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [selectedTax, setSelectedTax] = useState();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedConnectivity, setSelectedConnectivity] = useState([]); // Initialize as an array
  const [selectedPorts, setSelectedPorts] = useState([]);
  const [selectedSoftware, setSelectedSoftware] = useState([]);
  const [selectedAvailability, setSelectedAvailability] = useState([]);
  const [selectedDeliveryOption, setSelectedDeliveryOption] = useState([]);
  const [selectedInstalledApps, setSelectedInstalledApps] = useState([]);
  const [selectedSupport, setSelectedSupport] = useState([]);
  const [selectConditions, setSelectedConditions] = useState([]);
  const [selectedRefundType, setSelectedRefundType] = useState([]);
  const [finalPrice, setFinalPrice] = useState(0);
  const [fields, setFields] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [image, setImage] = useState(null);

  const handleAddField = () => {
    setFields([...fields, { key: "", value: "" }]);
  };

  const handleInputChange = (index, field, value) => {
    const newFields = [...fields];
    newFields[index][field] = value;
    setFields(newFields);
  };
  // Fetch Brand
  const fetchBrand = async () => {
    try {
      const response = await API.get("/brands");

      if (response.status === 200 && Array.isArray(response.data)) {
        setBrand(response.data); // Set the brand data properly
      } else {
        message.error("No brands found.");
      }
    } catch (error) {
      console.error("Failed to fetch brand:", error);
      message.error("Unable to load brand.");
    }
  };

  // Fetch Brand
  const fetchOffer = async () => {
    try {
      const response = await API.get("/offers");
      setOffer(response?.data || []);
    } catch (error) {
      console.error("Failed to fetch offer:", error);
      message.error("Unable to load offer.");
    }
  };

  // Fetch Brand
  const fetchStock = async () => {
    try {
      const response = await API.get("/stock");
      setStock(response?.data || []);
    } catch (error) {
      console.error("Failed to fetch stock:", error);
      message.error("Unable to load stock.");
    }
  };
  // Fetch Categories
  const fetchCategories = async () => {
    try {
      const response = await API.get("/category");
      setCategories(response?.data || []);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
      message.error("Unable to load categories.");
    }
  };

  const fetchTax = async () => {
    try {
      const taxResponse = await API.get("/taxes");
      setTaxes(taxResponse?.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("Failed to fetch data.");
    }
  };

  // Submit Handler
  const handleSubmit = async (values) => {
    setLoading(true);

    try {
      const detailsObject = fields.reduce((acc, field) => {
        if (field.key && field.value) {
          acc[field.key] = field.value;
        }
        return acc;
      }, {});

      const payload = {
        ...values,
        specifications: { ...values?.specifications, details: detailsObject },
        pricing: {
          ...values?.pricing,
          taxes: [
            {
              taxId: selectedTax?._id || initialValue.pricing.taxes[0].taxId,
              taxName:
                selectedTax?.taxName || initialValue.pricing.taxes[0].taxName,
              percentage:
                selectedTax?.percentage ||
                initialValue.pricing.taxes[0].percentage,
            },
          ],
        },
        image: image ? image : initialValue.image,
      };

      let res;
      if (isEdit) {
        res = await API.put(`/products/${initialValue?._id}`, payload);
        if (res.status === 200) {
          message.success(
            isEdit
              ? "Product updated successfully!"
              : "Product added successfully!"
          );
          onSubmit();
          setOpen(false);
          form.resetFields();
        }
      } else {
        // Add new product logic
        res = await API.post("/products", payload);
        if (res.status === 200) {
          message.success("Product added successfully!");
          onSubmit();
          setOpen(false);
          form.resetFields();
        }
      }
    } catch (error) {
      console.error("Submit failed:", error);
      message.error(error.response?.data?.message || "An error occurred.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      // Prefetch necessary data
      fetchCategories();
      fetchBrand();
      fetchOffer();
      fetchTax();

      if (isEdit && initialValue) {
        // Populate fields for edit mode
        form.setFieldsValue({
          ...initialValue,
          category: initialValue?.category?._id || undefined,
          brand: initialValue?.brand?._id || undefined,
          name: initialValue?.name,
          pricing: {
            salePrice: initialValue?.pricing?.salePrice || 0,
            mrpPrice: initialValue?.pricing?.mrpPrice || 0,
            discount: initialValue?.pricing?.discount || {
              value: 0,
              type: "percentage",
            },
          },
          taxes: initialValue?.pricing?.taxes[0]?.taxId || 0,
        });

        setFields(
          Object.entries(initialValue?.specifications?.details || {}).map(
            ([key, value]) => ({
              key,
              value,
            })
          )
        );

        setFinalPrice(initialValue?.pricing?.finalPrice || 0);
      } else {
        // Reset form for add mode
        form.resetFields();
        setFields([]);
        setFinalPrice(0);
      }
    }
  }, [isEdit, initialValue, open]);

  const handleRemoveField = (index) => {
    const newFields = fields.filter((_, i) => i !== index);
    setFields(newFields);
  };

  const handleConnectivity = (value) => {
    setSelectedConnectivity(value);
  };

  const handlePorts = (value) => {
    setSelectedPorts(value);
  };

  const handleSoftware = (value) => {
    setSelectedSoftware(value);
  };

  const handleAvailability = (value) => {
    setSelectedAvailability(value);
  };

  const handleDeliveryOption = (value) => {
    setSelectedDeliveryOption(value);
  };

  const handleInstalledApps = (value) => {
    setSelectedInstalledApps(value);
  };

  const handleSupport = (value) => {
    setSelectedSupport(value);
  };

  const handleCondition = (value) => {
    setSelectedConditions(value);
  };

  const handleRefundType = (value) => {
    setSelectedRefundType(value);
  };
  const handlePriceChange = () => {
    const price = parseFloat(form.getFieldValue("pricing?.price"));
    const discountType = form.getFieldValue("pricing?.discount?.type");
    const discountValue = parseFloat(
      form.getFieldValue("pricing?.discount?.value")
    );

    let discountedPrice = price;

    // Calculate discounted price if price is a valid number
    if (!isNaN(price)) {
      if (!isNaN(discountValue)) {
        if (discountType === "percentage") {
          discountedPrice = price - (price * discountValue) / 100;
        } else if (discountType === "flat") {
          discountedPrice = price - discountValue;
        }
      }
    }

    // Set the calculated final price in the form
    setFinalPrice(discountedPrice);
    form.setFieldsValue({
      "pricing?.finalPrice": discountedPrice.toFixed(2), // Update final price field
    });
  };

  useEffect(() => {
    // Automatically calculate the final price whenever price, discount type, or discount value changes
    handlePriceChange();
  }, [
    form.getFieldValue("pricing?.price"),
    form.getFieldValue("pricing?.discount?.type"),
    form.getFieldValue("pricing?.discount?.value"),
  ]);

  const handleChange = async (info) => {
    let updatedFileList = [...info.fileList];
    updatedFileList = updatedFileList.filter((file) => file.status !== "error");

    if (info.file.status === "done") {
      message.success(`${info.file.name} file uploaded successfully`);
      const imagePath = info.file.response.imagePath;
      setImage(imagePath);
      setFileList([info.file]);
    } else if (info.file.status === "error") {
      message.error(`${info.file.name} file upload failed.`);
    }
    setFileList(updatedFileList);
  };

  const handleRemoveImage = async (file) => {
    const productId = JSON.parse(localStorage.getItem("userData"))?.id;
    const fileUrl = file.url;
    const fileName = fileUrl.split("/").pop();

    try {
      const response = await API.delete(`/products/image/${fileName}`, {
        data: { imagePath: file.url },
      });

      if (response.status === 200) {
        message.success("Image removed successfully");
        setImage(null);
        setFileList([]);
      }
    } catch (error) {
      console.error("Error removing image:", error);
      message.error("Failed to remove image");
    }
  };

  const customRequest = async (options) => {
    const { file, onSuccess, onError } = options;
    const userId = JSON.parse(localStorage.getItem("userData"))?.id;

    const formData = new FormData();
    formData.append("previousImagePath", image); // Previous image path for replacement
    formData.append("userId", userId);
    formData.append("image", file);

    try {
      const response = await API.post("/products/upload", formData);
      message.success("Image uploaded successfully!");
      setImage(response.data.imagePath); // Update the image state

      onSuccess(response.data);

      // Update the file list with the new image URL
      setFileList([
        {
          uid: file.uid,
          name: file.name,
          status: "done",
          url: `http://localhost:5001/${response.data.imagePath}`, // New image URL
        },
      ]);

      // Update the form with the new image path as well
      form.setFieldsValue({
        images: [{ url: `http://localhost:5001/${response.data.imagePath}` }],
      });
    } catch (error) {
      console.error("Error uploading image:", error);
      onError(error);
      message.error("Failed to upload image.");
    }
  };

  return (
    <Drawer
      title={isEdit ? "Edit Product" : "Add Product"}
      width={1000}
      placement="right"
      onClose={() => setOpen(false)}
      open={open}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          ...initialValue,
          specifications: initialValue?.specifications || {},
          pricing: initialValue?.pricing || {
            price: 0,
            discount: {},
            finalPrice: 0,
          },
        }}
      >
        <Row gutter={[16, 16]}>
          {/* Row 1 */}
          <Col xs={24} md={12} lg={8}>
            <Form.Item
              label="Brand"
              name="brand"
              rules={[{ required: true, message: "Please select a brand!" }]}
            >
              <Select 
                placeholder="Select brand" 
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) => {
                  const label = typeof option?.children === "string" ? option.children : "";
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
              >
                {brand.length ? (
                  brand.map((brandItem) => (
                    <Option key={brandItem?._id} value={brandItem?._id}>
                      {brandItem?.name}
                    </Option>
                  ))
                ) : (
                  <Option disabled>No brands available</Option>
                )}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={12} lg={8}>
            <Form.Item
              label="Category"
              name="category"
              rules={[{ required: true, message: "Please select a category!" }]}
            >
              <Select 
                placeholder="Select category" 
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) => {
                  const label = typeof option?.children === "string" ? option.children : "";
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
              >
                {categories.map((category) => (
                  <Option key={category?._id} value={category?._id}>
                    {category?.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={12} lg={8}>
            <Form.Item label="Name" name={["specifications", "name"]}>
              <Input placeholder="Enter the name" />
            </Form.Item>
          </Col>

          {/* Row 2 */}
          <Col xs={24} md={12} lg={8}>
            <Form.Item
              label="GST"
              name="taxes"
              initialValue={initialValue?.pricing.taxes[0]?.taxId}
            >
              <Select
                placeholder="Select Tax"
                onChange={(value) => {
                  const selectedTax = taxes.find((tax) => tax._id === value);
                  setSelectedTax(selectedTax);
                }}
              >
                {taxes.map((taxes) => (
                  <Option key={taxes._id} value={taxes._id}>
                    {taxes.percentage} %
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={12} lg={8}>
            <Form.Item
              label="HSN ASC"
              name="hsn_sac"
              rules={[{ required: true, message: "Please enter HSN ASC!" }]}
            >
              <Input placeholder="Enter HSN ASC" />
            </Form.Item>
          </Col>

          <Col xs={24} md={12} lg={8}>
            <Form.Item label="Code" name="code">
              <Input placeholder="Enter Code" />
            </Form.Item>
          </Col>

          {/* Pricing Header */}
          <Col span={24}>
            <div style={{ fontSize: 20, fontWeight: "bold" }}>Pricing</div>
          </Col>

          {/* Row 3 */}
          <Col xs={24} md={12} lg={8}>
            <Form.Item
              label="MRP"
              name={["pricing", "mrpPrice"]}
              rules={[{ required: true, message: "Please enter the MRP!" }]}
            >
              <Input type="number" placeholder="Enter MRP" />
            </Form.Item>
          </Col>

          <Col xs={24} md={12} lg={8}>
            <Form.Item
              label="Sale Price"
              name={["pricing", "salePrice"]}
              rules={[
                { required: true, message: "Please enter the sale price!" },
              ]}
            >
              <Input type="number" placeholder="Enter Sale Price" />
            </Form.Item>
          </Col>

          <Col xs={24} md={12} lg={8}>
            <Form.Item
              label="Purchase Price"
              name={["pricing", "purchasePrice"]}
            >
              <Input type="number" placeholder="Enter Purchase Price" />
            </Form.Item>
          </Col>
        </Row>

        <Row justify="end" style={{ marginTop: "25px" }}>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            style={{ padding: "20px" }}
          >
            {isEdit ? "Update Product" : "Add Product"}
          </Button>
        </Row>
      </Form>
    </Drawer>
  );
};

export default ProductDrawer;
